import { getConfig } from 'config';
import { ApiConnectionContextProvider } from 'contexts/ApiConnectionContext';
import { ParamContextProvider, useParamContext } from 'contexts/ParamContext';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoIosArrowBack } from 'react-icons/io';
import { Navigate, useNavigate } from 'react-router-dom';
import { changeAxiosUrl } from 'utils/axios';

import Button from 'components/Buttons';
import CompanyAvatar from 'components/CompanyAvatar';
import { useAuthenticatedContext } from 'hooks/useAuthenticated';
import { COMPANY } from 'models/company.model';

import LoginForm from './LoginForm';
import { LOGIN_TYPES } from './LoginForm/LoginForm';
import * as Styled from './LoginOptionsPage.styles';

const LoginOptions = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const company = useParamContext<COMPANY>();

  const [loginType, setLoginType] = useState<LOGIN_TYPES | undefined>(undefined);
  const { isLoading, clearLoginError } = useAuthenticatedContext();

  useEffect(() => {
    localStorage.setItem('company', company);
    changeAxiosUrl(company);
  }, [company]);

  const changeLoginType = (type?: LOGIN_TYPES) => () => setLoginType(type);

  const handleBack = useCallback(() => {
    if (!loginType) {
      return navigate('/login');
    }

    clearLoginError();
    setLoginType(undefined);
  }, [clearLoginError, loginType, navigate]);

  const { name, color, Icon } = getConfig(company);

  return (
    <>
      <ApiConnectionContextProvider>
        <CompanyAvatar color={color} Icon={Icon} />
        <Styled.CompanyDetails>{name}</Styled.CompanyDetails>
        {loginType ? (
          <LoginForm company={company} loginType={loginType} />
        ) : (
          <Styled.ButtonsContainer>
            <Button $login $block onClick={changeLoginType(LOGIN_TYPES.MOBILE_APP)}>
              {t('loginPage.mobileAppMethod')}
            </Button>
            <Button $login $block onClick={changeLoginType(LOGIN_TYPES.PASSWORD)}>
              {t('loginPage.passwordMethod')}
            </Button>
          </Styled.ButtonsContainer>
        )}
      </ApiConnectionContextProvider>
      <Styled.BackButton
        type="link"
        htmlType="button"
        data-cy="form_submit"
        onClick={handleBack}
        icon={<IoIosArrowBack />}
        disabled={isLoading}
      >
        {t('loginPage.back')}
      </Styled.BackButton>
    </>
  );
};

const LoginOptionsPage = () => (
  <ParamContextProvider paramName="company" enumName={COMPANY} fallback={<Navigate to="/login" />}>
    <LoginOptions />
  </ParamContextProvider>
);

export default LoginOptionsPage;
